import type {
	EmptyChatLaunchAction,
	SelectedPinnedItem,
	SelectedSpaceSource,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';

/** Parameters passed to the onChatEmptyLaunch callback when a user clicks "Chat" on a space card. */
export interface ChatEmptyLaunchParams {
	spaceId: string;
	spaceName: string;
	/** Emoji ID for the space, used to display the space icon in the SpacePill. */
	spaceEmoji: string;
	/** Greeting title shown in the empty chat state (e.g. "Chat about anything in this space"). */
	greetingTitle: string;
	iconSrc?: string;
	iconSrcDark?: string;
	/** Custom conversation actions to show in the empty chat state. */
	customConversationActions?: EmptyChatLaunchAction[];
	/** When true, custom actions completely replace the default provider actions instead of prepending to them. */
	replaceProviderActions?: boolean;
	/** Skill registry tags used to fetch skill metadata for hover cards on the custom actions. */
	skillRegistryTags?: string[];
	/**
	 * Sources to seed onto the launched space's `selectedSpace.sources` so the newly-created
	 * space shows its originating thread's source(s) immediately instead of waiting for a
	 * `GET /spaces` refetch.
	 */
	sources?: SelectedSpaceSource[];
	/**
	 * Set instead of the space fields above when the launch is scoped to a pinned Jira Epic,
	 * Atlas Goal, or Atlas Project rather than a Rovo Space. When set, `handleChatEmptyLaunch`
	 * builds project context from this entity instead of `spaceId`/`spaceName`, and does not
	 * treat the launch as space-scoped (no space pill, no space-landing navigation).
	 */
	selectedPinnedItem?: SelectedPinnedItem;
	/**
	 * The tab the launch was initiated from. Currently unused by the launch handler
	 * (the origin tab is captured directly from the live `activeTabId`); retained on
	 * the type/callers pending a follow-up cleanup.
	 */
	launchTabId?: typeof SUGGESTIONS_TAB_ID | typeof FOR_YOU_TAB_ID;
}

/**
 * Duplicated locally to avoid a cross-package import from
 * `@atlassian/conversation-assistant` (which already depends on
 * `@atlassian/rovo-for-you`, so importing back would form a circular
 * workspace dependency). Keep the string values in sync with
 * `packages/ai-mate/conversation-assistant/src/ui/chat-history/rovo-tabs/constants.ts`.
 */
export const SUGGESTIONS_TAB_ID = 'suggestions';
export const FOR_YOU_TAB_ID = 'for-you';

export interface ForYouPanelProps {
	/** The tenant cloud ID for GraphQL queries. */
	cloudId: string;
	/** The Atlassian Account ID of the current user. */
	userId: string;
	/** Host product, forwarded to the insights banner and the create-space `x-product` header. */
	product?: string;
	/** Whether the For You tab is currently the active/visible tab. Analytics events only fire when active. */
	isActive?: boolean;
	/** Callback invoked when user clicks "Chat" on a project card. Opens an empty chat with project context. */
	onChatEmptyLaunch?: (params: ChatEmptyLaunchParams) => void;
}
